import { gql } from 'apollo-boost';
import { ProjectQueries } from "@src/queries/ProjectQueries";


export const fragments = {

    ACTIVITY: {
        QUERY_RETURN:    `id name displayName description`,
        MUTATION_RETURN: `id name displayName description`,
        EDIT_FIELDS:     `
			id
			name
			description
			displayName
			refIdCode

			budgetValueConditions{
				id
				targetIntakeQuestionId
				conditionType
				answerValue
				budgetValue
			}
			defaultBudgetValue

			defaultTimeSpanUnit
			defaultTimeSpanValue
			projectTimingMilestoneOffsetMonths
			timeSpanConditions {
				id
				targetIntakeQuestionId
				conditionType
				answerValue
				timeSpanUnit
				timeSpanValue
			}

			duplicationQuestions{ id name inputType dataValueSetId }
			childActivities { childActivityId parentCompletionRequired parentSchedulingEnforced }
			parentActivities { parentActivityId parentCompletionRequired parentSchedulingEnforced }
			triggeringSubObjectives{ id }

			subCategoryId

            businessFunctionId
			defaultOperationalCost
			defaultOngoingCostMin
			defaultOngoingCostMax
			defaultOngoingCostDurationQuarters
			professionalFees{ professionalFeeId }
			hires{
				headcount
				hireTypeId
				isHiringPoint
			}

			defaultCriticality

			tags{ tagName }
			isRequired
			perceivedAsContinuous
		`
    },

    STATIC_PROJECT_ACTIVITY:              {
        QUERY_RETURN:           () => `
			id
			startDate
			endDate
			name
			activity{ id }
		`,
        MUTATION_RETURN:        () => `
			id
			startDate
			endDate
			name
			activity{ id }
		`,
        USER_EDIT_FIELDS_VITAL: () => `
			id
			notes
			completionState
			activity { id }
			
		    scheduledBudgetItems{
                id
                name
                amount
                note
            }
			perceivedAsContinuous
			
			requiredHires{
			    hireTypeId
			    headcount
			}
		`,
        USER_EDIT_FIELDS:       () => `
			id
			name
            description
            businessFunctionId
            
            criticality
            percentDone
            isAtRisk
            
            startDate
            endDate
            constraintDate
            constraintType
            deadlineDate
            manuallyScheduled
           
            scheduledBudgetItems {
                id
                name
                amount
                note
                quarter
                year
            }
            
            requiredHires {
               headcount
               hireTypeId
            }
		`,
        GANTT_QUERY_FIELDS:     () => `
            id
            name
            description
            businessFunctionId
            businessFunction { id name description colorValue iconName isArchived resourceCode }
            notes
            corvalType
            
            startDate
            endDate
            deadlineDate
            duration
            constraintDate
            constraintType
            completionState
            manuallyScheduled
            
            perceivedAsContinuous
            
            isAtRisk
            criticality
            percentDone
            
            categoryId
            categoryName
            categoryDescription
            categoryOrdinal
            
            subCategoryId
            subCategoryName
            subCategoryDescription
            subCategoryOrdinal
            
            subObjectiveId
            subObjectiveName
            subObjectiveDescription
            subObjectiveOrdinal
            
            scheduledBudgetItems {
                id
                name
                amount
                note
                quarter
                year
            }
            
            requiredHires {
                headcount
                hireTypeId
                hireType { 
                    id 
                    name 
                    businessFunctionId
                    businessFunction { id name }
                }
            }
        `,
    },
    STATIC_PROJECT_ACTIVITY_WATCHERS:     {
        QUERY_RETURN: `
			account{ firstName, lastName }
		`,
    },
    STATIC_PROJECT_ACTIVITY_DEPENDENCIES: {
        QUERY_RETURN:       () => `id from to`,
        GANTT_QUERY_FIELDS: () => `
            id
            from
            to
            type
            lag
            lagUnit
            active
        `,
    },
    STATIC_PROJECT_MILESTONE_RELATIONSHIPS: {
        QUERY_RETURN: () => `
            milestoneRelationships {
                active
                lag
                lagUnit
                projectVersionMilestone {
                    ${ ProjectQueries.fragments.PROJECTS.MILESTONE_FIELDS }
                }
            }`
    },
    STATIC_PROJECT_MILESTONE_TASKS:       {
        GANTT_QUERY_FIELDS: () => `
            ${ ProjectQueries.fragments.PROJECTS.MILESTONE_FIELDS }
        `,
    },
}

export const queries = {

    GET_PROJECT_ACTIVITIES: ( returnQueryString = fragments.STATIC_PROJECT_ACTIVITY.QUERY_RETURN() ) => {

        const query = `
            query GetProjectActivities(
                $projectVersionId:  Uuid!,
                $order:             [ StaticProjectActivitySortInput! ]
                $where:             StaticProjectActivityFilterInput
            ){    
                staticProjectActivities (
                    order:              $order,
                    where:              $where,
                    projectVersionId:   $projectVersionId
                ) {
    
                    ${ returnQueryString }
                }
            }`

        return gql( query );
    },

    GET_ACTIVITIES: ( returnQueryString = fragments.ACTIVITY.QUERY_RETURN ) => {

        const query = `
            query GetActivities( $order: [ActivitySortInput!], $where: ActivityFilterInput ){
                activities( order: $order, where: $where) {
                    ${ returnQueryString }
                }
            }`

        return gql( query );
    },

    GET_ACTIVITY: ( returnQueryString = fragments.ACTIVITY.QUERY_RETURN ) => {
        const query = `
        query GetActivity( $id: Uuid! ){
            activities( where: { id: { eq: $id } } ) {
                ${ returnQueryString }
            }
        }`
        return gql( query );
    },

    CHECK_ACTIVITY_DEPENDENCY_LOOP_DETECTION: () => {
        const query = `
        query GetActivityLoopDetection( $parentActivityIds:[Uuid!]!, $childActivityIds:[Uuid!]!, $tracePaths:Boolean!  ){
            activityRelationshipLoops(
                parentActivityIds: $parentActivityIds,
                childActivityIds: $childActivityIds,
                tracePaths: $tracePaths,
            ) {
                loopsDetected
                paths {
                    activityId
                    step
                }
            }
        }`
        return gql( query );
    },
}

export const mutations = {

    CREATE_ACTIVITY: ( returnQueryString = fragments.ACTIVITY.MUTATION_RETURN ) => {

        const query = `
        mutation CreateActivity( $input: ActivityCreateCommandInput! ){
            createActivity( input: $input ) {
                ${ returnQueryString }
            }
        }`
        return gql( query );
    },

    UPDATE_ACTIVITY: ( returnQueryString = fragments.ACTIVITY.MUTATION_RETURN ) => {

        const query = `
        mutation UpdateActivity( $input: ActivityUpdateCommandInput! ){
            updateActivity( input: $input ) {
                ${ returnQueryString }
            }
        }`
        return gql( query );
    },

    DELETE_ACTIVITY: () => {

        const query = `
        mutation DeleteActivity( $input: ActivityDeleteCommandInput! ){
            deleteActivity( input: $input )
        }`

        return gql( query );
    },

    ADD_WATCHER_TO_PROJECT_ACTIVITY: ( returnQueryString = fragments.STATIC_PROJECT_ACTIVITY_WATCHERS.QUERY_RETURN ) => {
        const query = `
        mutation AddWatcherToProjectActivity( $input:ProjectActivityAccountCreateCommandInput! ){
            createProjectActivityAccount( input: $input )  {
                ${ returnQueryString }
            }
        }`
        return gql( query );
    },

    REMOVE_WATCHER_FROM_PROJECT_ACTIVITY: ( returnQueryString = fragments.STATIC_PROJECT_ACTIVITY_WATCHERS.QUERY_RETURN ) => {
        const query = `
        mutation RemoveWatcherFromProjectActivity( $input:ProjectActivityAccountDeleteCommandInput! ){
            deleteProjectActivityAccount( input: $input )  {
                ${ returnQueryString }
            }
        }`
        return gql( query );
    },
}

export const functions = {}

export const ActivityQueries = {
    fragments,
    queries,
    mutations,
    functions,
}
